import SellingUnit from "@/components/SellingUnit";
import React from "react";
import { Link } from "react-router-dom";
import { Star, Heart, Eye, ShoppingCart } from "lucide-react";
import { useL, useLang } from "@/i18n";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { money } from "@/lib/api";
import { cldUrl } from "@/lib/img";
import { toast } from "sonner";

export function Stars({ value = 0, count, size = 14 }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} className={i <= Math.round(value) ? "fill-gold text-gold" : "text-navy/20"} />
      ))}
      {count != null && <span className="ml-1 text-xs text-muted-foreground">({count})</span>}
    </span>
  );
}

export function SectionHeading({ overline, title, action, center }) {
  return (
    <div className={`mb-8 flex flex-wrap items-end justify-between gap-4 ${center ? "flex-col items-center text-center" : ""}`}>
      <div>
        {overline && <p className="mb-2 text-xs font-semibold uppercase tracking-[0.25em] text-sunset">{overline}</p>}
        <h2 className="font-display text-3xl font-bold text-navy sm:text-4xl">{title}</h2>
      </div>
      {action}
    </div>
  );
}

export function ProductCard({ product }) {
  const L = useL();
  const { t } = useLang();
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const onSale = product.sale_price && product.sale_price < product.price;
  const soldOut = product.stock <= 0;
  const isNew = (product.tags || []).includes("new");

  return (
    <div
      data-testid={`product-card-${product.slug}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-cream-card card-shadow transition-all duration-300 hover:-translate-y-1.5 hover:card-shadow-lg"
    >
      <div className="absolute left-3 top-3 z-10 flex flex-col gap-1.5">
        {onSale && <span className="rounded-full bg-sunset px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">{t("common.sale")}</span>}
        {isNew && <span className="rounded-full bg-palm px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">{t("common.new")}</span>}
        {product.made_in_haiti && <span className="rounded-full bg-navy px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-gold">100% Ayiti</span>}
      </div>
      <button
        data-testid={`wishlist-toggle-${product.slug}`}
        onClick={() => { toggle(product.id); toast.success(has(product.id) ? t("wishlist.removed") : t("wishlist.added")); }}
        className="absolute right-3 top-3 z-10 grid h-9 w-9 place-items-center rounded-full glass transition hover:scale-110"
        aria-label="wishlist"
      >
        <Heart size={17} className={has(product.id) ? "fill-sunset text-sunset" : "text-navy"} />
      </button>

      <Link to={`/product/${product.slug}`} className="relative block aspect-square overflow-hidden bg-cream">
        <img
          src={cldUrl(product.images?.[0], { w: 500 })}
          alt={L(product, "name")}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-x-0 bottom-0 flex translate-y-full items-center justify-center gap-2 p-3 transition-transform duration-300 group-hover:translate-y-0">
          <span
            data-testid={`quickview-${product.slug}`}
            className="flex items-center gap-1.5 rounded-full glass px-3 py-2 text-xs font-semibold text-navy shadow"
          >
            <Eye size={14} /> {t("common.quickView")}
          </span>
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-4">
        <Stars value={product.rating} count={product.review_count} />
        <Link to={`/product/${product.slug}`} className="mt-1.5">
          <h3 className="font-head text-[15px] font-semibold leading-snug text-navy line-clamp-2 transition-colors group-hover:text-sunset">
            {L(product, "name")}
          </h3>
        </Link>
        <SellingUnit product={product} />
        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{L(product, "description")}</p>
        <div className="mt-3 flex items-end justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-head text-lg font-bold text-navy">{money(onSale ? product.sale_price : product.price)}</span>
            {onSale && <span className="text-sm text-muted-foreground line-through">{money(product.price)}</span>}
          </div>
        </div>
        <button
          data-testid={`add-to-cart-${product.slug}`}
          disabled={soldOut}
          onClick={() => { addItem(product); toast.success(`${L(product, "name")} — ${t("common.addToCart")}`); }}
          className={`mt-3 flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition ${soldOut ? "cursor-not-allowed bg-muted text-muted-foreground" : "btn-gold"}`}
        >
          <ShoppingCart size={15} />
          {soldOut ? t("common.outOfStock") : t("common.addToCart")}
        </button>
      </div>
    </div>
  );
}
